import dayjs from 'dayjs'

/**
 * 공지 팝업 설정.
 *
 * 점검 안내 같은 공지는 문구와 일정이 매번 달라지는데, 코드에 두면 그때마다
 * 재빌드·재배포를 해야 한다. 설치 판정 기준(signalConfig)과 같은 방식으로
 * 서버의 JSON 파일만 교체하면 공지가 바뀌도록 분리한다.
 *
 * 파일이 없거나 형식이 어긋나면 공지를 띄우지 않는다 — 판정 기준과 달리
 * 공지는 "안 뜨는 것"이 안전한 실패다.
 */
export interface NoticeHighlight {
  /** 박스 상단 배지 문구 (예: 점검 일시 안내) */
  label: string
  /** 강조 1행 (예: 2026년 8월 14일(금요일)) */
  date: string
  /** 강조 2행 (예: 12:00 ~ 13:00) */
  time: string
  /** 박스 하단 보조 설명 */
  note: string
}

export interface NoticeConfig {
  /** 공지 사용 여부. 문구를 남겨둔 채 껐다 켰다 할 수 있도록 별도 필드로 둔다. */
  enabled: boolean
  /**
   * 공지 식별자.
   *
   * "다시 보지 않기" 기록이 이 값에 묶인다. 새 공지를 올릴 때 반드시 바꿔야
   * 이전 공지를 숨긴 사용자에게도 새 공지가 뜬다.
   */
  id: string
  /** 노출 시작 시각. null 이면 제한 없음. */
  startAt: string | null
  /** 노출 종료 시각. null 이면 제한 없음. 지나면 자동으로 사라진다. */
  endAt: string | null
  title: string
  /** 본문. 줄 단위 배열이라 JSON 에서 개행을 다루지 않아도 된다. */
  body: string[]
  /** 강조 박스. 필요 없으면 null. */
  highlight: NoticeHighlight | null
  /**
   * "다음 공지까지 보지 않기" 체크박스 노출 여부.
   *
   * 끄면 매 접속마다 뜬다. 반드시 봐야 하는 공지에 쓴다.
   */
  allowDismiss: boolean
}

/** 설정 파일 경로. docker-compose 볼륨 마운트 대상이라 임의로 바꾸지 않는다. */
export const NOTICE_CONFIG_PATH = '/config/notice-config.json'

/**
 * 숨김 기록 저장 키. 값은 마지막으로 숨긴 공지의 id 다.
 *
 * 기간이 아니라 공지 번호로 기억하므로, 새 공지가 올라오면(= id 가 바뀌면)
 * 숨김이 저절로 풀린다.
 */
const DISMISS_STORAGE_KEY = 'notice_dismissed_id'

/** 설정 파일이 없을 때의 기본값. 공지를 띄우지 않는 상태다. */
export const DEFAULT_NOTICE_CONFIG: NoticeConfig = {
  enabled: false,
  id: '',
  startAt: null,
  endAt: null,
  title: '',
  body: [],
  highlight: null,
  allowDismiss: true,
}

export interface NoticeConfigStatus {
  /** default = 설정 파일 없이 동작 중, remote = 설정 파일 적용됨 */
  source: 'default' | 'remote'
  loadedAt: Date | null
  /** 마지막 실패 사유. 서버에 접속하지 않고 원인을 확인해야 하므로 남겨둔다. */
  error: string | null
}

let currentConfig: NoticeConfig = DEFAULT_NOTICE_CONFIG
let currentStatus: NoticeConfigStatus = {source: 'default', loadedAt: null, error: null}

/**
 * 이번 방문에서 이미 닫은 공지.
 *
 * "다시 보지 않기"를 체크하지 않으면 계속 안내해야 하지만, 페이지를 옮길 때마다
 * 다시 뜨면 화면을 쓸 수 없다. 그래서 닫으면 이번 방문 동안만 접어두고,
 * 새로고침하거나 다시 접속하면 또 뜨게 한다. 저장소에 남기지 않는 이유다.
 */
let closedInThisVisit: string | null = null

export const getNoticeConfig = (): NoticeConfig => currentConfig
export const getNoticeConfigStatus = (): NoticeConfigStatus => currentStatus

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

/** null 이거나 파싱 가능한 시각 문자열이어야 한다. */
const parseMoment = (value: unknown): dayjs.Dayjs | null | false => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value !== 'string') return false
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : false
}

const parseHighlight = (raw: any): NoticeHighlight | null | string => {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'object') return 'highlight 는 객체이거나 null 이어야 합니다.'

  const {label, date, time, note} = raw
  const fields = {label, date, time, note}
  const invalid = Object.entries(fields).find(
    ([, value]) => value !== undefined && typeof value !== 'string'
  )
  if (invalid) return `highlight.${invalid[0]} 는 문자열이어야 합니다.`

  return {
    label: label ?? '',
    date: date ?? '',
    time: time ?? '',
    note: note ?? '',
  }
}

/**
 * 외부 JSON 을 검증해 NoticeConfig 로 변환한다.
 * 한 군데라도 어긋나면 통째로 거부한다 — 문구 절반만 반영된 공지가 더 나쁘다.
 *
 * @returns 유효하면 설정, 아니면 사유 문자열
 */
export const parseNoticeConfig = (raw: any): NoticeConfig | string => {
  if (!raw || typeof raw !== 'object') return '설정 형식이 올바르지 않습니다.'

  const {enabled, id, startAt, endAt, title, body, highlight, allowDismiss} = raw

  if (typeof enabled !== 'boolean') return 'enabled 는 true/false 여야 합니다.'
  if (typeof id !== 'string' || id.trim() === '') return 'id 는 비어 있을 수 없습니다.'
  if (typeof title !== 'string') return 'title 은 문자열이어야 합니다.'
  if (!isStringArray(body)) return 'body 는 문자열 배열이어야 합니다.'
  if (typeof allowDismiss !== 'boolean') return 'allowDismiss 는 true/false 여야 합니다.'

  const start = parseMoment(startAt)
  if (start === false) return 'startAt 형식을 해석할 수 없습니다.'
  const end = parseMoment(endAt)
  if (end === false) return 'endAt 형식을 해석할 수 없습니다.'
  if (start && end && end.isBefore(start)) return 'endAt 은 startAt 보다 뒤여야 합니다.'

  const parsedHighlight = parseHighlight(highlight)
  if (typeof parsedHighlight === 'string') return parsedHighlight

  /* 켜진 공지에 제목이 없으면 빈 팝업이 뜬다 */
  if (enabled && title.trim() === '') return '공지를 사용하려면 title 이 필요합니다.'

  return {
    enabled,
    id: id.trim(),
    startAt: start ? start.toISOString() : null,
    endAt: end ? end.toISOString() : null,
    title,
    body,
    highlight: parsedHighlight,
    allowDismiss,
  }
}

/** 편집 UI 에서 저장할 때 쓰는 직렬화. */
export const stringifyNoticeConfig = (config: NoticeConfig): string =>
  JSON.stringify(
    {
      enabled: config.enabled,
      id: config.id,
      startAt: config.startAt,
      endAt: config.endAt,
      title: config.title,
      body: config.body,
      highlight: config.highlight,
      allowDismiss: config.allowDismiss,
    },
    null,
    2
  )

/**
 * 설정 파일을 읽어 적용한다.
 * 어떤 실패에서도 예외를 던지지 않고, 공지를 띄우지 않는 쪽으로 넘어간다.
 */
export const loadNoticeConfig = async (timeoutMs: number = 3000): Promise<NoticeConfig> => {
  /* 통신이 느린 현장에서 응답을 기다리며 멈추지 않도록 제한을 둔다 */
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const res = await fetch(`${NOTICE_CONFIG_PATH}?ts=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
    const contentType = res.headers.get('content-type') ?? ''

    /* 파일이 없으면 nginx SPA fallback 이 index.html 을 200 으로 돌려주므로
       상태 코드만으로는 판별할 수 없다. */
    if (!res.ok || !contentType.includes('json')) {
      currentStatus = {...currentStatus, error: '설정 파일을 찾을 수 없습니다.'}
      return currentConfig
    }

    const parsed = parseNoticeConfig(await res.json())
    if (typeof parsed === 'string') {
      currentStatus = {...currentStatus, error: parsed}
      return currentConfig
    }

    currentConfig = parsed
    currentStatus = {source: 'remote', loadedAt: new Date(), error: null}
  } catch {
    currentStatus = {...currentStatus, error: '설정 파일을 불러오지 못했습니다.'}
  } finally {
    clearTimeout(timer)
  }
  return currentConfig
}

/** 이 공지를 "다음 공지까지 보지 않기"로 숨겨두었는지. */
const isDismissed = (config: NoticeConfig): boolean => {
  try {
    /* 공지가 바뀌면(= id 가 달라지면) 이전 숨김 기록은 저절로 무효가 된다 */
    return localStorage.getItem(DISMISS_STORAGE_KEY) === config.id
  } catch {
    /* 저장소를 못 읽으면 숨기지 않는다 — 공지를 놓치는 쪽이 더 손해다 */
    return false
  }
}

/** 지금 이 공지를 띄워야 하는지. */
export const shouldShowNotice = (config: NoticeConfig): boolean => {
  if (!config.enabled || config.id === '') return false
  if (closedInThisVisit === config.id) return false

  const now = dayjs()
  if (config.startAt && now.isBefore(dayjs(config.startAt))) return false
  if (config.endAt && now.isAfter(dayjs(config.endAt))) return false

  return !isDismissed(config)
}

/**
 * 공지를 닫는다.
 *
 * @param keepHidden "다음 공지까지 보지 않기" 체크 여부.
 *                   체크하지 않으면 이번 방문 동안만 접어둔다.
 */
export const dismissNotice = (config: NoticeConfig, keepHidden: boolean) => {
  closedInThisVisit = config.id

  if (!keepHidden || !config.allowDismiss) return
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, config.id)
  } catch {
    /* 저장 실패는 다음 접속에 다시 뜨는 정도의 문제라 무시한다 */
  }
}

/** 편집 화면에서 미리 보기 위해 이 기기의 숨김 기록을 지운다. */
export const clearNoticeDismissal = () => {
  closedInThisVisit = null
  try {
    localStorage.removeItem(DISMISS_STORAGE_KEY)
  } catch {
    /* 무시 */
  }
}
